import { useRouter } from 'next/router'
import { parseCookies, setCookie } from 'nookies'
import { useCallback, useEffect } from 'react'

import {
  UTM_ATTRIBUTION_COOKIE_NAME,
  UTM_COOKIE_MAX_AGE_SECONDS,
  UTM_INFLOW_SESSION_STORAGE_KEY,
  getUtmAttributionCache,
  getUtmInflowKey,
  parseUtmFromQuery,
  resolveUtmAttribution,
  serializeUtmAttribution,
  setUtmAttributionCache,
} from 'features/tracking/lib/utm'
import { GTM_EVENT_EXTERNAL_INFLOW } from 'lib/gtag'

const pushExternalInflow = (utm: ReturnType<typeof parseUtmFromQuery>) => {
  if (!utm || typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: GTM_EVENT_EXTERNAL_INFLOW,
    inflow_source: utm.utm_source,
    inflow_medium: utm.utm_medium ?? null,
    inflow_campaign: utm.utm_campaign ?? null,
    inflow_content: utm.utm_content ?? null,
  })
}

export const useUtmTracking = () => {
  const router = useRouter()
  // Stabilize effect deps: router.query is a new object every render.
  const queryKey = JSON.stringify(router.query)

  useEffect(() => {
    if (!router.isReady) return

    const incoming = parseUtmFromQuery(router.query)
    if (!incoming) return

    // Cookie: last-touch attribution for enrichment on later GTM events (survives tab close).
    setCookie(null, UTM_ATTRIBUTION_COOKIE_NAME, serializeUtmAttribution(incoming), {
      maxAge: UTM_COOKIE_MAX_AGE_SECONDS,
      path: '/',
      secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
      sameSite: 'lax',
    })
    setUtmAttributionCache(incoming)

    // sessionStorage: primary dedup for external_inflow — fire once per browser session
    // even when the 30-day cookie already matches (returning visit from the same source).
    const inflowKey = getUtmInflowKey(incoming)
    try {
      if (sessionStorage.getItem(UTM_INFLOW_SESSION_STORAGE_KEY) === inflowKey) {
        return
      }
      sessionStorage.setItem(UTM_INFLOW_SESSION_STORAGE_KEY, inflowKey)
    } catch {
      // Ignore sessionStorage failures (private mode / SSR edge cases).
    }

    pushExternalInflow(incoming)
    // queryKey mirrors router.query; reading router.query avoids a JSON.parse round-trip.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryKey stabilizes router.query identity
  }, [router.isReady, queryKey])

  const getUtmAttribution = useCallback(() => {
    const cached = getUtmAttributionCache()
    if (cached !== undefined) return cached

    const cookies = parseCookies()
    return resolveUtmAttribution(cookies[UTM_ATTRIBUTION_COOKIE_NAME])
  }, [])

  return { getUtmAttribution }
}
